import React, { useState, useEffect } from 'react';
import { Search, X, Plus, FileText } from 'lucide-react';
import './AddScriptToBriefModal.css';

function AddScriptToBriefModal({ isOpen, onClose, onAddScript, brief, campaignScripts }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedScripts, setSelectedScripts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter out scripts that are already assigned to this brief and exclude drafts
  const availableScripts = campaignScripts.filter(script => {
    if (!brief || !brief.scripts || !Array.isArray(brief.scripts)) return true;
    
    // Check if script is already assigned to this brief
    const isAlreadyAssigned = brief.scripts.some(briefScript => 
      briefScript.id === script.id || briefScript.name === script.name
    );
    
    // Exclude drafts and already assigned scripts
    return !isAlreadyAssigned && !script.isDraft;
  });

  // Filter scripts based on search term
  const filteredScripts = availableScripts.filter(script => {
    const scriptName = script.name || script.title || `Script ${script.id}`;
    const scriptDescription = script.description || '';
    
    return scriptName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           scriptDescription.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleScriptSelect = (script) => {
    setSelectedScripts(prev => {
      const isSelected = prev.some(s => s.id === script.id);
      if (isSelected) {
        return prev.filter(s => s.id !== script.id);
      } else {
        return [...prev, script];
      }
    });
  };

  const handleAddScripts = async () => {
    if (selectedScripts.length === 0) return;
    
    setIsLoading(true);
    try {
      await onAddScript(selectedScripts);
      onClose();
    } catch (error) {
      console.error('Error adding scripts to brief:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedScripts([]);
    setSearchTerm('');
    onClose();
  };

  if (!isOpen || !brief) return null;

  return (
    <div className="add-script-to-brief-modal-overlay" onClick={handleClose}>
      <div className="add-script-to-brief-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="add-script-to-brief-modal-header">
          <h3 className="add-script-to-brief-modal-title">Add Scripts to Brief</h3>
          <button className="add-script-to-brief-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="add-script-to-brief-modal-content">
          {/* Search */}
          <div className="script-search-section">
            <div className="search-input-container">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search scripts by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="script-search-input"
              />
            </div>
          </div>

          {/* Brief Info */}
          <div className="brief-info-section">
            <h4>Adding scripts to: <span className="brief-name">{brief?.name || brief?.title || 'Untitled Brief'}</span></h4>
            <p className="available-scripts-count">
              {filteredScripts.length} available script{filteredScripts.length !== 1 ? 's' : ''} from this campaign
            </p>
          </div>

          {/* Scripts List */}
          <div className="scripts-list-container">
            {filteredScripts.length === 0 ? (
              <div className="no-scripts-found">
                <div className="no-scripts-icon">📝</div>
                <h4>No scripts available</h4>
                <p>
                  {availableScripts.length === 0 
                    ? campaignScripts.some(s => s.isDraft) 
                      ? "Only draft scripts available. Complete a script to assign it to this brief"
                      : "All campaign scripts are already assigned to this brief"
                    : "No scripts match your search criteria"
                  }
                </p>
              </div>
            ) : (
              <div className="scripts-list">
                {filteredScripts.map((script) => {
                  const isSelected = selectedScripts.some(s => s.id === script.id);
                  return (
                    <div
                      key={script.id}
                      className={`script-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleScriptSelect(script)}
                    >
                      <div className="script-item-checkbox">
                        <div className={`checkbox ${isSelected ? 'checked' : ''}`}>
                          {isSelected && <Plus size={12} />}
                        </div>
                      </div>
                      <div className="script-item-content">
                        <div className="script-item-header">
                          <h5 className="script-item-title">
                            {script.name || script.title || `Script ${script.id}`}
                          </h5>
                        </div>
                        <p className="script-item-description">
                          {script.description || 'No description available'}
                        </p>
                        <div className="script-item-meta">
                          <div className="script-meta-item">
                            <FileText size={14} />
                            <span>{script.creatorsCount || 0} creators</span>
                          </div>
                          <div className="script-meta-item">
                            <span>{script.videosCount || 0} videos</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="add-script-to-brief-modal-footer">
          <button
            className="cancel-btn"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            className="add-scripts-btn"
            onClick={handleAddScripts}
            disabled={selectedScripts.length === 0 || isLoading}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner"></div>
                Adding Scripts...
              </>
            ) : (
              <>
                <Plus size={16} />
                Add {selectedScripts.length > 0 ? `${selectedScripts.length} ` : ''}Script{selectedScripts.length !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddScriptToBriefModal;













